import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

interface AppRolesScenarioProps {
  onBack: () => void;
}

const AppRolesScenario: React.FC<AppRolesScenarioProps> = ({ onBack }) => {
  return (
    <Box>
      <Typography variant="h5" gutterBottom color="primary">
        Create and Test App Roles
      </Typography>
      
      <Typography variant="body1" paragraph>
        This scenario guides you through creating application roles in Microsoft Entra ID, assigning users to those roles,
        and verifying the role claims in tokens. App roles are a powerful way to implement role-based access control (RBAC)
        in your applications.
      </Typography>
      
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
          Why App Roles Matter
        </Typography>
        <Typography variant="body2">
          App roles provide a secure, standardized way to implement authorization in your applications.
          Unlike custom claims or groups, app roles are specifically designed for application-level permissions
          and are fully supported by Microsoft identity libraries and frameworks.
        </Typography>
      </Box>
      
      <Stepper orientation="vertical">
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Open App Registration in AppConfig²</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Navigate to the Applications section and locate the application for which you want to create roles.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Create App Roles</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Now let's create two example roles: "Reader" and "Administrator":
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Navigate to 'App roles' section" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Create app role'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Configure the first role with these values:" 
                  secondary={
                    <Box component="span" sx={{ display: 'block', mt: 1 }}>
                      • Display name: Reader<br/>
                      • Value: Reader<br/>
                      • Description: Can read application data<br/>
                      • Allowed member types: Users/Groups<br/>
                      • Do you want to enable this app role: Yes
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Apply'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Repeat to create the Administrator role" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
              <Typography variant="subtitle2" gutterBottom>
                Best Practice: Role Values
              </Typography>
              <Typography variant="body2">
                Use simple, descriptive values for roles like "Reader" instead of complex strings like "application.read.all".
                Role values are case-sensitive and will appear exactly as entered in token claims.
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Apply Changes</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Save your app role configuration:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Apply Changes' or 'Save'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Wait for the confirmation message" />
              </ListItem>
            </List>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Assign Users to App Roles</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Now let's assign users to the roles we created:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Navigate to the 'Enterprise Applications' section in Microsoft Entra ID" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Find your application in the list" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select 'Users and groups'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click '+ Add user/group'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select users to assign (including yourself for testing)" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Under 'Select a role', choose one of your app roles" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Assign'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Repeat for additional role assignments" />
              </ListItem>
            </List>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Configure API Access (if your app has an API)</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              If your application exposes an API, make sure app roles are exposed to client applications:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Return to the App Registration" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Go to 'Expose an API'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="If needed, add scopes that correspond to your roles" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Consider mapping roles to scopes (e.g., Reader role -> api.read scope)" />
              </ListItem>
            </List>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Verify ID Token Claims</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Let's verify the role claims in the ID token:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In AppConfig², go to the 'Test' tab for your application" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select 'Authenticate as User'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Complete the authentication flow using a user who has been assigned a role" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="View the decoded ID token" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Look for the 'roles' claim" 
                  secondary="This should be an array containing the role values you assigned to the user"
                />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
              <Typography variant="subtitle2" gutterBottom>
                Expected Format
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                "roles": ["Reader", "Administrator"]
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Verify Access Token Claims</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Now let's verify the role claims in the access token:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the test section, select 'Get Token for API'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select your API's scope from the dropdown" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Get Token'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="View the decoded access token" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Look for the 'roles' claim" 
                  secondary="This should match what you saw in the ID token"
                />
              </ListItem>
            </List>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Using Roles in Your Application</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Here's how you can use these roles in your application code:
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                JavaScript/TypeScript (MSAL.js)
              </Typography>
              <Box component="pre" sx={{ 
                p: 1, 
                bgcolor: '#2b2b2b', 
                color: '#f8f8f2', 
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.8rem'
              }}>
{`// Check if user has a specific role
function hasRole(idTokenClaims, roleName) {
  if (!idTokenClaims.roles) {
    return false;
  }
  return idTokenClaims.roles.includes(roleName);
}

// Usage
const account = msalInstance.getActiveAccount();
if (account) {
  const isAdmin = hasRole(account.idTokenClaims, "Administrator");
  const isReader = hasRole(account.idTokenClaims, "Reader");
  
  if (isAdmin) {
    // Show admin features
  } else if (isReader) {
    // Show reader features
  } else {
    // Show limited UI
  }
}`}
              </Box>
            </Box>
            
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                C# (ASP.NET Core)
              </Typography>
              <Box component="pre" sx={{ 
                p: 1, 
                bgcolor: '#2b2b2b', 
                color: '#f8f8f2', 
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.8rem'
              }}>
{`// In Startup.cs configure authorization policies
services.AddAuthorization(options =>
{
    options.AddPolicy("RequireAdministratorRole",
        policy => policy.RequireRole("Administrator"));
    
    options.AddPolicy("RequireReaderRole",
        policy => policy.RequireRole("Reader"));
});

// In controller
[Authorize(Policy = "RequireAdministratorRole")]
public IActionResult AdminDashboard()
{
    return View();
}

[Authorize(Policy = "RequireReaderRole")]
public IActionResult ViewData()
{
    return View();
}`}
              </Box>
            </Box>
            <Box sx={{ mb: 2, mt: 2 }}>
              <Button variant="contained" size="small">Finish</Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
      
      <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Common Issues & Solutions
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText 
              primary="Roles not appearing in tokens" 
              secondary="Role assignments can take 15-30 minutes to propagate. Try signing out and in again." 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText 
              primary="Roles visible in ID token but not access token" 
              secondary="Ensure you've configured the API to accept app roles and properly setup scopes." 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText 
              primary="Role assignment errors" 
              secondary="Only Global Administrators or Application Administrators can assign roles." 
            />
          </ListItem>
        </List>
      </Box>
      
      <Box sx={{ mt: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowRightIcon />}
          onClick={onBack}
        >
          Back to All Scenarios
        </Button>
      </Box>
    </Box>
  );
};

export default AppRolesScenario;